import type { Amount } from '@skippay/core/@types/amount';
import type { ApplicationBase } from '@skippay/core/@types/applicationBase';
import { toFloat } from '@skippay/core/utils/amountUtils';
import type { Paginated } from '@skippay/core/utils/queryUtils';
import type { Maybe } from '@skippay/core/utils/types';

import { capNames, toIsoDate } from 'jarabot/utils';
import AllUnclosedApplicationsQuery from 'queries/AllUnclosedApplicationsQuery.graphql';
import MyInstallmentsContractsQuery from 'queries/MyInstallmentsContractsQuery.graphql';
import { nearestUnpaidInstallment } from 'utils/payInThreeUtils';

import { LIST_CAPS } from '../../constants';
import JarabotPayInThreeQuery from '../../queries/JarabotPayInThreeQuery.graphql';
import type { DeferredPaymentItem, InstallmentItem, PayInThreeItem } from '../../types';
import { INSTALLMENTS_ACTIVE_FILTER, PAY_IN_THREE_ACTIVE_FILTER } from '../filters';
import type { DomainSource } from '../types';

interface ApplicationRow {
	isPayable?: ApplicationBase['isPayable'];
	eshopAdditionalInfo?: ApplicationBase['eshopAdditionalInfo'];
	state?: ApplicationBase['state'];
	stateReason?: ApplicationBase['stateReason'];
	order?: ApplicationBase['order'];
	financing?: Pick<
		NonNullable<ApplicationBase['financing']>,
		'currentDebt' | 'payInThree' | 'deferredPayment'
	>;
}

export const payInThreeSource: DomainSource<{
	myContractApplications?: Paginated<ApplicationRow>;
}> = {
	domains: ['payInThree'],
	query: JarabotPayInThreeQuery,
	variables: {
		limit: LIST_CAPS.payInThree,
		sort: '-createdDate',
		filter: PAY_IN_THREE_ACTIVE_FILTER,
	},
	toModel: (data) => {
		const rows = data.myContractApplications?.data ?? [];
		const payInThree: PayInThreeItem[] = rows.map((app) => {
			const next = nearestUnpaidInstallment(app.financing?.payInThree?.installments ?? []);
			return {
				eshop: app.eshopAdditionalInfo?.name ?? undefined,
				state: app.state ?? undefined,
				stateReason: app.stateReason ?? undefined,
				total: app.order?.totalPrice ? toFloat(app.order.totalPrice) : undefined,
				debt: app.financing?.currentDebt ? toFloat(app.financing.currentDebt) : undefined,
				next: next
					? {
							date: toIsoDate(next.dueDate),
							amount: next.balance ? toFloat(next.balance) : undefined,
						}
					: undefined,
				daysOverdue: next?.currentDpd ?? undefined,
				canPay: app.isPayable ?? undefined,
				items: app.order?.items
					? capNames(
							app.order.items.map((item) => item.name),
							LIST_CAPS.orderItemNames
						)
					: undefined,
			};
		});
		return { payInThree };
	},
};

export const deferredPaymentsSource: DomainSource<{
	unclosedApplications?: Paginated<ApplicationRow>;
}> = {
	domains: ['deferredPayments'],

	query: AllUnclosedApplicationsQuery,
	toModel: (data) => {
		const rows = data.unclosedApplications?.data ?? [];
		const deferredPayments: DeferredPaymentItem[] = rows
			.slice(0, LIST_CAPS.deferredPayments)
			.map((app) => {
				const debt = app.financing?.currentDebt ? toFloat(app.financing.currentDebt) : undefined;

				return {
					eshop: app.eshopAdditionalInfo?.name ?? undefined,
					state: app.state ?? undefined,
					stateReason: app.stateReason ?? undefined,
					total: app.order?.totalPrice ? toFloat(app.order.totalPrice) : undefined,
					debt,
					dueDate: toIsoDate(app.financing?.deferredPayment?.dueDate),
					amountToPay: debt,
					daysOverdue: app.financing?.deferredPayment?.currentDpd ?? undefined,
					canPay: app.isPayable ?? undefined,
					items: app.order?.items
						? capNames(
								app.order.items.map((item) => item.name),
								LIST_CAPS.orderItemNames
							)
						: undefined,
				};
			});
		return { deferredPayments };
	},
};

interface InstallmentContractRow {
	loanType?: Maybe<string>;
	state?: Maybe<string>;
	nextInstallment?: {
		dueDate?: Maybe<string>;
		balance?: Maybe<Amount>;
		daysPastDueDate?: Maybe<number>;
	} | null;
	maximumPayment?: Maybe<Amount>;
}

export const installmentsSource: DomainSource<{
	myInstallmentsContracts?: Paginated<InstallmentContractRow>;
}> = {
	domains: ['installments'],
	query: MyInstallmentsContractsQuery,
	variables: { limit: LIST_CAPS.installments, filter: INSTALLMENTS_ACTIVE_FILTER },
	toModel: (data) => {
		const rows = data.myInstallmentsContracts?.data ?? [];
		const installments: InstallmentItem[] = rows.map((contract) => {
			const next = contract.nextInstallment;
			return {
				type: contract.loanType ?? undefined,
				state: contract.state ?? undefined,
				next: next
					? {
							date: toIsoDate(next.dueDate),
							amount: next.balance ? toFloat(next.balance) : undefined,
						}
					: undefined,
				daysOverdue: next?.daysPastDueDate ?? undefined,
				earlyRepayment: contract.maximumPayment ? toFloat(contract.maximumPayment) : undefined,
			};
		});
		return { installments };
	},
};
